// lib
import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { CUSTOM_QUESTION_REG } from '@aytm/app/stats/page/components/lib/constants'
import { hasComments } from '@aytm/app/stats/page/components/lib/utils'
import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import { getModelValue } from '@aytm/app/stats/lib/stat-page'
import { calcVerbatimDownloadParams } from '@aytm/app/stats/page/question-views/components/controls/lib/export-params'

// components
import ViewModes from '@aytm/app/stats/page/question-views/oe/components/controls/ViewModes'
import VerbatimControls from '@aytm/app/stats/page/question-views/oe/components/controls/Verbatim'
import TagsControls from '@aytm/app/stats/page/question-views/oe/components/controls/Tags'
// import WordcloudControls from '@aytm/app/stats/page/question-views/oe/components/controls/WordCloud'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'

// styles
import style from '@aytm/app/stats/page/question-views/oe/components/styles/Controls.module.scss'

const ControlsComponents = ({
  model,
  data,
  features,
  disabled,
  isVerbatimType,
  isSurveyGuest,
  onViewModeChange,
  onConfigChange,
  onExport
}) => {
  const { id, type, items, viewMode, questionChartMode } = model
  // the cloud is not drawn when the report or the question shows tables only, so its own controls,
  // export included, go with it
  const cloudShown = 'wordcloud' == viewMode && !isNoneChartMode(questionChartMode, getModelValue('chartMode'))

  const exportOptions = useMemo(() => {
    const options = []

    const allowed = ['oe', 'oex', 'cn'].includes(type) || null === items || hasComments(items)
    if (allowed && !features.oe_tags_management.disabled) {
      options.push({ key: 'csv-verbatim', text: 'Download all verbatim (CSV)' })
      if (!CUSTOM_QUESTION_REG.test(String(id).split('-')[0])) {
        options.push({ key: 'csv-verbatim-codes', text: 'Download all verbatim with codes (CSV) ' })
      }
    }

    return options
  }, [id, type, items, features.oe_tags_management.disabled])

  const onExportClick = (key, callback) => {
    const [fileType, exportType, exportExtra] = key.split('-')

    const download = calcVerbatimDownloadParams({ key: model.key, fileType, exportType, exportExtra })
    if (!download) return callback?.()

    onExport(download.fileType, download.exportType, download.exportOptions, callback)
  }

  return disabled ? null : (
    <>
      {<ViewModes model={model} data={data} onViewModeChange={onViewModeChange} />}
      {'verbatim' == viewMode ? (
        <VerbatimControls
          model={model}
          data={data}
          features={features}
          isSurveyGuest={isSurveyGuest}
          onConfigChange={onConfigChange}
        />
      ) : null}
      {'tags' == viewMode ? <TagsControls model={model} onConfigChange={onConfigChange} /> : null}
      {cloudShown ? null : isVerbatimType && !isSurveyGuest ? (
        <div className="p-right">
          <ExportDropdown options={exportOptions} onExport={onExportClick} />
        </div>
      ) : null}
      <div
        id={`oe-controls-${id}`}
        className={`${style.portal}${'wordcloud' == viewMode ? '' : ' ' + style.hidden}`}
      ></div>
    </>
  )
}

ControlsComponents.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  isVerbatimType: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  onViewModeChange: PropTypes.func,
  onConfigChange: PropTypes.func,
  onExport: PropTypes.func
}

ControlsComponents.displayName = 'ControlsComponents'

export default ControlsComponents
